<template>
  <div>
    <SearchFilters
      :searchQuery="searchQuery"
      :showSearchBox="showSearchBox"
      @businessChange="handleBusinessChange"
      @branchChange="handleBranchChange"
      @update:searchQuery="searchQuery = $event"
    />

    <b-container fluid>
      <b-row class="mb-3">
        <b-col cols="12" md="8" class=" ">
          <div class="iq-card ">
            <div class="iq-card-header ">
              <div class="iq-header-title">
                <h4 class="card-title">Floor List</h4>
              </div>
            </div>

            <div class="iq-card-body">
              <b-overlay :show="loading" rounded="sm" variant="Primary"></b-overlay>
              <b-table  
                class="table b-table table-striped"
                :items="filteredLocalizedItems"
                :fields="columns.map(col => col.prop)"
                striped
                ref="table"
              >
                <template v-for="column in columns" #[`cell(${column.prop})`]="data" >
                  <span v-if="!column.isAction">
                    {{ data.item[column.prop] || column.default }}
                  </span>

                  <span v-else>
                    <div class="d-flex justify-content-around">
                      <router-link
                        :to=" `/floors/${data.item.id}/tables?rid=${businessId}&bid=${branchId}`"
                        class="btn iq-bg-primary btn-sm iq-waves-effect text-uppercase"
                      >
                        <i class="fas fa-eye " />
                      </router-link>
                      <b-button @click="editMenu(data.item)" variant=" iq-bg-success mr-1 " size="sm" >
                        <i class="ri-ball-pen-fill"></i>
                      </b-button>
                      <b-button @click="handleDelete(data.item)" v-b-modal.modal-delete variant=" iq-bg-danger" size="sm" >
                        <i class="ri-delete-bin-line"></i>
                      </b-button>
                    </div>
                  </span>
                </template>
              </b-table>
              <div
                class="mt-3 d-flex justify-content-center"
                v-if="!this.searchQuery"
              >
                <b-pagination
                  class="iq-page-primary"
                  v-if="paginationMeta.total > paginationMeta.per_page"
                  v-model="paginationMeta.current_page"
                  :total-rows="paginationMeta.total"
                  :per-page="paginationMeta.per_page"
                  @input="FloorsList"
                />
              </div>
              <EmptySearchMessage
                v-if="searchQuery && filteredLocalizedItems.length <= 0"
              />
            </div>
          </div>
        </b-col>
        <b-col cols="6" md="4" class="">
          <div class="iq-card ">
            <div></div>
            <Create
              v-if="showAdd"
              :isInList="true"
              @onSubmit="FloorsList()"
              :branchId="branchId"
              :businessId="businessId"
            />

            <Edit
              v-if="!showAdd && editModel"
              :isInList="true"
              @onSubmit="FloorsList()"
              :model="editModel"
              :showAdd="showAdd"
              @showAddChanged="updateShowAddState"
              :branchId="branchId"
              :businessId="businessId"
            />
          </div>
        </b-col>
      </b-row>
    </b-container>
    <!-- Delete Modal -->
    <b-modal
      id="modal-delete"
      title="Confirm Delete"
      @ok="confirmDelete"
      ok-title="Yes, Delete"
      cancel-title="Cancel"
      ok-variant="danger"
      centered
    >
      <p>Are you sure you want to delete this item?</p>
    </b-modal>
  </div>
</template>

<script>
import Create from "./Create.vue";
import TableConfiguration from "./TableConfiguration";
import Edit from "./Edit.vue";
import { core } from "@/config/pluginInit";
import { deleteFloor, getFloorsList } from "@/API/FloorsAPIs";
import SearchFilters from "@/components/SearchFilters.vue";
import EmptySearchMessage from "@/components/EmptySearchMessage.vue";
import { submitAndNotify } from "@/helpers/utils";

export default {
  name: "FloorsList",
  mixins: [TableConfiguration],
  components: {
    Create,
    Edit,
    SearchFilters,
    EmptySearchMessage,
  },
  data() {
    return {
      loading: false,
      items: [],
      searchQuery: "",
      deletedItem: null,
      showAdd: true,
      editModel: null,
      showSearchBox: true,
      businessId: null,
      branchId: null,
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    filteredLocalizedItems() {
      return this.items
        .map(item => {
          const localizedData =
            item.locales.find(locale => locale.locale === this.currentLocale) ||
            item.locales[0];
          return {
            ...item,
            name: localizedData.name || "",
            description: localizedData.description || ""
          };
        })
        .filter(item =>
          item.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
    },
    userBusinesses() {
      return this.$store.state.User.user.business || [];
    },
    userBranches() {
      return this.userBusinesses.find(b => b.id === this.businessId)?.branches || [];
    },
  },
  watch: {
    userBusinesses: {
      handler(newBusinesses) {
        if (newBusinesses.length > 0 && !this.businessId) {
          this.businessId = newBusinesses[0].id;
        }
      },
      deep: true,
      immediate: true,
    },
    userBranches: {
      handler(newBranches) {
        if (newBranches.length > 0 && !this.branchId) {
          this.branchId = newBranches[0].id;
          if (this.branchId && this.businessId) {
        this.FloorsList();
      }
        }
      },
      deep: true,
    },
  },
  mounted() {
    core.index();
  },
  methods: {
    handleBusinessChange(newValue) {
      this.businessId = newValue;
    },
    handleBranchChange(newValue) {
      this.branchId = newValue;
      this.FloorsList();
    },
    handleDelete(model) {
      this.deletedItem = model;
    },
    confirmDelete() {
      if (this.deletedItem) {
        const callback = () => {
          this.FloorsList();
        };
        submitAndNotify(
          this,
          deleteFloor({
            businessId: this.businessId,
            branchId: this.branchId,
            id: this.deletedItem.id
          }),
          callback
        );
      }
    },
    async FloorsList(page = 1) {
      if (!this.businessId || !this.branchId) return;
      this.loading = true;
      try {
        const perPage = this.searchQuery
          ? this.paginationMeta.total
          : this.paginationMeta.per_page;
        const response = await getFloorsList({
          businessId: this.businessId,
          branchId: this.branchId,
          "per-page": perPage,
          page
        });
        this.items = response?.data;
        this.paginationMeta = response.meta;
      } catch (error) {
        console.error(error);
        this.items = [];
      }finally {
      this.loading = false;
    }
    },
    editMenu(model) {
      this.showAdd = false;
      const requiredLocales = ["en", "ar"];
      this.editModel = {
        ...model,
        locales: requiredLocales.map(
          locale =>
            model.locales.find(l => l.locale === locale) || {
              locale,
              name: "",
              description: "",
              id: ""
            }
        )
      };
    },
    updateShowAddState(newValue) {
      this.showAdd = newValue;
    }
  }
};
</script>

<style scoped></style>